<template>
  <div>
    <app-table
      ref="app-table"
      :is-current-page="false"
      :page-size="2000"
      :table-props="{ 'max-height': '450px' }"
      :table-key="table_key"
      :table-list-data="table_data"
    />
  </div>
</template>

<script>
import { CHANNEL_INTERFACE_TYPE } from '@/enums';
import { addDeliveryContentApi, pullDeliveryContentApi } from '@/api/tool/delivery-content';

export default {
  components: {
    'app-table': () => import('@/components/app-table'),
  },
  props: {
    // 表单项配置
    formItem: {
      type: Object,
      required: true,
    },
    // 初始化数据
    initOps: {
      type: Object,
      required: true,
    },
    // 数据表
    dataMap: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      /** 表头配置 */
      table_key: require('./model/table-key.js')(this),
      /** 表格数据 */
      table_data: [],
    };
  },
  computed: {
    advertiser_ids() {
      const { advertiser_ids } = this.initOps;
      return advertiser_ids;
    },
    channel_interface() {
      const { channel_interface } = this.initOps;
      return channel_interface;
    },
  },
  created() {
    if (Object.keys(this.formItem.value.result_map).length) {
      this.edit();
    } else {
      this.generateTableData();
    }
  },
  methods: {
    /**
     * 生成表格数据
     * @returns {void}
     */
    generateTableData() {
      const data = this.advertiser_ids.map(advertiser_id => {
        return {
          advertiser_id,
          type: { options: this.formItem.value.type_selection, value: '' },
          target: { ...this.initTarget() },
          add_config: {
            is_add: false,
            is_loading: false,
            value: '',
            name: '',
          },
        };
      });
      this.table_data.push(...data);
    },
    /**
     * 编辑
     * @returns {void}
     */
    edit() {
      const data = Object.entries(this.formItem.value.result_map).map(([key, value]) => {
        return {
          advertiser_id: key,
          type: {
            options: this.formItem.value.type_selection,
            value: value.type,
          },
          target: {
            ...this.initTarget(),
            [value.type]: {
              options: value.options,
              value: value.value,
            },
          },
          add_config: {
            is_add: false,
            is_loading: false,
            value: '',
            name: '',
          },
        };
      });
      this.table_data.push(...data);
    },
    /**
     * 获取目标配置
     * @param {string} type 目标类型
     * @param {Object} row 表格行数据
     * @returns {void}
     */
    async getTargetConfig(type, row) {
      const result = await this.dataMap[type]?.(row.advertiser_id, type);
      Object.assign(row.target[type], { options: result, value: '' });
    },
    /**
     * 初始化目标
     * @returns {Object}
     */
    initTarget() {
      const target = {};
      this.formItem.value.type_selection.forEach(({ value }) => {
        Object.assign(target, {
          [value]: {
            options: [],
            value: '',
          },
        });
      });
      return target;
    },
    /**
     * 新建
     * @param {Object} row 行数据
     */
    async onAdd(row) {
      const { advertiser_id, type, add_config } = row;
      if (!add_config.value) {
        this.$message.warning(this.inputIdPlaceholder(type.value));
        return;
      }
      if (this.isSupportAddName() && !add_config.name) {
        this.$message.warning(this.inputNamePlaceholder(type.value));
        return;
      }
      try {
        add_config.is_loading = true;
        const strategy = {
          [CHANNEL_INTERFACE_TYPE.BAIDU]: () => {
            return {
              baidu_params: {
                name: add_config.name,
              },
            };
          },
          default: () => {},
        };
        await addDeliveryContentApi({
          channel_interface: this.channel_interface,
          advertiser_id,
          delivery_platform_type: type.value,
          app_id: add_config.value,
          ...(strategy[this.channel_interface] || strategy['default'])(),
        });
        await this.getTargetConfig(type.value, row);
        this.$message.success('新建成功');
        add_config.is_add = false;
      } finally {
        add_config.is_loading = false;
      }
    },
    /**
     * 同步
     * @param {Object} row 行数据
     */
    async onPull(row) {
      const { advertiser_id, type } = row;
      const { message } = await pullDeliveryContentApi({
        channel_interface: this.channel_interface,
        advertiser_ids: [advertiser_id],
        delivery_platform_type: type.value,
      });
      await this.getTargetConfig(type.value, row);
      this.$message.success(message || '同步成功');
    },
    /**
     * 是否支持新建
     * @returns {boolean}
     */
    isSupportAdd() {
      const support_channel = [CHANNEL_INTERFACE_TYPE.OPPO, CHANNEL_INTERFACE_TYPE.HUAWEI, CHANNEL_INTERFACE_TYPE.BAIDU];
      return support_channel.includes(this.channel_interface);
    },
    /**
     * 是否支持同步
     * @returns {boolean}
     */
    isSupportPull() {
      const strategy = {
        [CHANNEL_INTERFACE_TYPE.BILIBILI]: () => {
          /**
           * 满足以下条件之一，支持同步
           * - 投放平台为【IOS应用(4)】
           */
          const delivery_platform_type = this.formItem.value.type_selection[0].value;
          const is_ios = delivery_platform_type === '4';

          return is_ios;
        },
        default: () => {
          const support_channel = [CHANNEL_INTERFACE_TYPE.HUAWEI, CHANNEL_INTERFACE_TYPE.VIVO, CHANNEL_INTERFACE_TYPE.JRTT, CHANNEL_INTERFACE_TYPE.XIAOMI];
          return support_channel.includes(this.channel_interface);
        },
      };
      return (strategy[this.channel_interface] || strategy['default'])();
    },
    /**
     * 投放内容新建是否支持名称
     * @returns {boolean}
     */
    isSupportAddName() {
      const support_channel = [CHANNEL_INTERFACE_TYPE.BAIDU];
      return support_channel.includes(this.channel_interface);
    },
    /**
     * ID输入框占位文本
     * @returns {string}
     */
    inputIdPlaceholder(delivery_platform_type) {
      const strategy = {
        [CHANNEL_INTERFACE_TYPE.OPPO]: () => {
          const delivery_platform_type_map = {
            '2': '请输入应用id',
            '3': '请输入快应用id',
          };

          return delivery_platform_type_map[delivery_platform_type];
        },
        default: () => '请输入应用id',
      };

      return (strategy[this.channel_interface] || strategy['default'])();
    },
    /**
     * 名称输入框占位文本
     * @returns {string}
     */
    inputNamePlaceholder() {
      const strategy = {
        default: () => '请输入应用名称',
      };

      return (strategy[this.channel_interface] || strategy['default'])();
    },
  },
};
</script>
